import {useState} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {QueryClient, dehydrate} from '@tanstack/react-query';
import {Button, Container, Flex, Input, Textarea, InputProps, Link, Text, useToast} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {setApiLocale} from '@eksab/api';
import {useRegionSettings} from '@eksab/hooks';

import {useContactMessageMutation} from '@eksab/features/contact/hooks';
import {SUPPORT_EMAIL} from '@eksab/const';

const initialFormData = {
  sender_name: '',
  sender_email: '',
  sender_phone_number: '',
  message: '',
};

type FormField = keyof typeof initialFormData;

export default function Contact() {
  const toast = useToast();
  const [formData, setFormData] = useState(initialFormData);

  const t = useTranslate();
  const contactMessageMutation = useContactMessageMutation();

  const onInputChange = (name: FormField) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData((prev) => ({...prev, [name]: e.target.value}));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement | HTMLDivElement>) => {
    e.preventDefault();

    contactMessageMutation.mutate(formData, {
      onSuccess: () => {
        setFormData(initialFormData);
        toast({title: t('contact.message.success'), status: 'success'});
      },
      onError: (e) => toast({status: 'error', title: e.response?.data.message ?? t('app.errors.try-again')}),
    });
  };

  return (
    <>
      <Head>
        <meta name="description" content="Predict game score and win prizes | Eksab" />
        <title>{`${t('contact.title')} | ${t('app.name')}`}</title>
      </Head>

      <Container py={py} flexDir="column" gap={[6, 10, 12, 16]} textAlign="center">
        <Flex as="section" flexDir="column" align="center" gap={[4, 6, 8]}>
          <Text as="h2" fontSize="2xl" fontWeight="700">
            {t('contact.title')}
          </Text>

          <Flex as="form" flexDir="column" gap={[3, null, 4]} w="full" maxW="33.5rem" onSubmit={onSubmit}>
            <InputField
              placeholder={t('app.fields.name')}
              value={formData.sender_name}
              onChange={onInputChange('sender_name')}
            />

            <InputField
              placeholder={t('app.fields.email')}
              type="email"
              inputMode="email"
              value={formData.sender_email}
              onChange={onInputChange('sender_email')}
            />

            <InputField
              placeholder={t('app.fields.phone_number')}
              type="tel"
              inputMode="tel"
              value={formData.sender_phone_number}
              onChange={onInputChange('sender_phone_number')}
            />

            <Textarea
              placeholder={t('app.fields.message')}
              value={formData.message}
              onChange={onInputChange('message')}
              rows={4}
            />

            <Button
              type="submit"
              alignSelf="center"
              fontSize="md"
              fontWeight="700"
              w="60"
              isLoading={contactMessageMutation.isLoading}
              isDisabled={
                contactMessageMutation.isLoading ||
                !formData.sender_name ||
                !formData.sender_email ||
                !formData.sender_phone_number ||
                !formData.message
              }
            >
              {t('app.actions.send')}
            </Button>
          </Flex>
        </Flex>

        <Flex as="section" flexDir="column" align="center" gap="2">
          <Text as="h2" fontSize="md" fontWeight="500">
            {t('contact.send-email', {
              br: (chunks) => (
                <>
                  <br />

                  <Text mt="1" fontSize="2xl" fontWeight="700">
                    {chunks}
                  </Text>
                </>
              ),
            })}
          </Text>

          <Link lang="en" href={`mailto:${SUPPORT_EMAIL}`} fontSize="md" fontWeight="700" color="#6c6c6c">
            {SUPPORT_EMAIL}
          </Link>
        </Flex>
      </Container>
    </>
  );
}

const InputField = (props: InputProps) => (
  <Input fontSize="md" fontWeight="500" rounded="xl" px="6" py="3.5" h="unset" {...props} />
);

export const getStaticProps: GetStaticProps = async ({locale}) => {
  setApiLocale(locale);

  const queryClient = new QueryClient();
  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};
